import {createActions, handleActions} from 'redux-actions';
import {combineReducers} from 'redux';
import {push} from 'react-router-redux';
import {actions as dirtyActions} from '../dirty.state';
import handleError from '../handleError';
import jsonFetch from '../jsonFetch';
import uuid from 'uuid/v4';
import {descCompareNumbers, keyBy} from '../arrayUtil';

export const standardActions = createActions(
	{},
	'SET_LIST_RETRIEVING',
	'SET_LIST_RETRIEVED',
	'SET_UPDATE',
	'SET_SAVING',
	'SET_SAVED',
	'SET_SAVE_CLEARED',
	'SET_NEW',
	'SET_NEW_SAVED',
	'SET_REMOVE_NEW',
	'VERSION_LIST_RETRIEVING',
	'VERSION_LIST_RETRIEVED',
	'VERSION_UPDATE',
	'VERSION_SAVING',
	'VERSION_SAVED',
	'VERSION_SAVE_CLEARED',
	'VERSION_SAVE_CANCELLED',
	'VERSION_NEW',
	'VERSION_NEW_SAVED',
	'VERSION_REMOVE_NEW',
	'CATEGORIES_RETRIEVED',
	'CATEGORIES_ADD'
);

export const actions = {
	...standardActions,
	
	retrieveCategories: () => dispatch => {
		jsonFetch(`${process.env.REACT_APP_API}/category`)
			.then(json => dispatch(standardActions.categoriesRetrieved(json)));
	},

	saveSet: setID => (dispatch, getState) => {
		const {syllabus: {sets}} = getState();
		
		const thisSet = sets.items.find(item => setID === 'new' ? item.isNew : item.uuid === setID);
		
		if (!thisSet) {
			handleError(dispatch)(`Could not find set with ID ${setID}`);
			return;
		}
		
		dispatch(standardActions.setSaving(setID));
		
		const body = {
			name: thisSet.name,
			copyright: thisSet.copyright,
			imagePath: thisSet.imagePath,
			region: thisSet.region,
			category: thisSet.category,
			displayTemplate: thisSet.displayTemplate,
			availableTo: thisSet.availableTo,
			hiddenFrom: thisSet.hiddenFrom,
			allowMods: thisSet.allowMods
		};
		
		if (thisSet.category) {
			dispatch(standardActions.categoriesAdd(thisSet.category));
		}

		if (thisSet.isNew) {
			body.uuid = uuid();
		}
		
		jsonFetch(thisSet.isNew ? `${process.env.REACT_APP_API}/set` : `${process.env.REACT_APP_API}/set/${setID}`, {
			method: thisSet.isNew ? 'POST' : 'PUT',
			body: JSON.stringify(body)
		})
			.then(() => {
				dispatch(dirtyActions.dirtyClear(setID));
				dispatch(standardActions.setSaved(null));
				if (thisSet.isNew) {
					dispatch(standardActions.setUpdate({
						setID: 'new',
						uuid: body.uuid
					}));
					dispatch(standardActions.setNewSaved(null));
					dispatch(push(`/set/${body.uuid}`)); // Go back to the list
				}
			})
			.catch(handleError(dispatch))
	},
	cancelSet: setID => (dispatch, getState) => {
		if (setID === 'new') {
			dispatch(standardActions.setRemoveNew());
			dispatch(dirtyActions.dirtyClear(setID));
			dispatch(push('/set')); // Go back to the list
			return;
		}
		
		const {syllabus: {sets}} = getState();
		
		const thisSet = sets.items.find(item => item.uuid === setID);
		
		if (!thisSet) {
			handleError(dispatch)(`Could not find set with ID ${setID}`);
			return;
		}
		
		dispatch(standardActions.setSaving(setID));
		
		// Get clean state from back-end
		jsonFetch(`${process.env.REACT_APP_API}/set/${setID}`)
			.then(json => {
				dispatch(dirtyActions.dirtyClear(setID));
				dispatch(standardActions.setSaveCleared());
				dispatch(standardActions.setUpdate({...json, setID}));
				dispatch(push('/')); // Go back to the list
			})
			.catch(handleError(dispatch))
	},
	
	saveVersion: (setID, versionID) => (dispatch, getState) => {
		const {syllabus: {versions}} = getState();
		
		const thisVersion = versions.items.find(item => versionID === 'new' ? item.isNew : item.uuid === versionID);
		
		if (!thisVersion) {
			handleError(dispatch)(`Could not find version with ID ${versionID}`);
			return;
		}
		
		dispatch(standardActions.versionSaving(versionID));
		
		const body = {
			name: thisVersion.name,
			number: thisVersion.number,
			status: thisVersion.status,
			approvals: thisVersion.approvals,
			notes: thisVersion.notes,
			// TA-10485 Important we include items array if defined regardless of length
			items: thisVersion.items ? thisVersion.items.map(item => ({
				...item,
				// TA-10485 Important we include items content array if defined regardless of length
				content: item.content ? item.content.map(content => ({
					...content,
					line: undefined
				})) : undefined
			})) : undefined,
			content: thisVersion.content
		};
		
		if (thisVersion.isNew) {
			body.uuid = uuid()
		}
		
		jsonFetch(thisVersion.isNew ? `${process.env.REACT_APP_API}/set/${setID}/version` : `${process.env.REACT_APP_API}/set/${setID}/version/${versionID}`, {
			method: thisVersion.isNew ? 'POST' : 'PUT',
			body: JSON.stringify(body)
		})
			.then(() => {
				dispatch(dirtyActions.dirtyClear(versionID));
				dispatch(standardActions.versionSaved(null));
				if (thisVersion.isNew) {
					dispatch(standardActions.versionUpdate({
						versionID: 'new',
						uuid: body.uuid
					}));
					dispatch(standardActions.versionNewSaved(null));
					dispatch(push(`/set/${setID}/version/${body.uuid}/details`)); // Go back to the list
				}
			})
			.catch(e => {
				dispatch(standardActions.versionSaveCancelled());
				handleError(dispatch)(e);
			})
	},
	
	cancelVersion: (setID, versionID) => (dispatch, getState) => {
		if (versionID === 'new') {
			dispatch(standardActions.versionRemoveNew());
			dispatch(dirtyActions.dirtyClear(versionID));
			dispatch(push(`/set/${setID}`)); // Go back to the list
			return;
		}
		
		const {syllabus: {versions}} = getState();
		
		const thisVersion = versions.items.find(item => item.uuid === versionID);
		
		if (!thisVersion) {
			handleError(dispatch)(`Could not find version with ID ${versionID}`);
			return;
		}
		
		dispatch(standardActions.versionSaving(versionID));
		
		// Get clean state from back-end
		jsonFetch(`${process.env.REACT_APP_API}/set/${setID}/version/${versionID}`)
			.then(json => {
				dispatch(dirtyActions.dirtyClear(versionID));
				dispatch(standardActions.versionSaveCleared());
				dispatch(standardActions.versionUpdate({...json, versionID}));
				dispatch(push(`/set/${setID}`)); // Go back to the list
			})
			.catch(handleError(dispatch))
	},
};

const sets = handleActions({
	[actions.setListRetrieving]: (state, {payload}) => ({
		...state,
		items: [],
		retrieving: true
	}),
	[actions.setListRetrieved]: (state, {payload}) => ({
		...state,
		items: [...payload],
		retrieving: false
	}),
	[actions.setSaving]: (state, {payload}) => ({
		...state,
		saving: payload
	}),
	[actions.setSaved]: state => ({
		...state,
		saving: null,
		lastSave: new Date().getTime()
	}),
	[actions.setSaveCleared]: state => ({
		...state,
		saving: null
	}),
	[actions.setNew]: state => ({
		...state,
		items: [...state.items.filter(item => !item.isNew), {
			uuid: 'new',
			name: 'New',
			copyright: '',
			imagePath: null,
			region: 'Australia',
			displayTemplate: '',
			availableTo: '',
			hiddenFrom: '',
			allowMods: false,
			isNew: true
		}]
	}),
	[actions.setNewSaved]: state => ({
		...state,
		items: state.items.map(item => {
			if (item.isNew) {
				return {
					...item,
					isNew: undefined
				};
			} else {
				return item;
			}
		})
	}),
	[actions.setRemoveNew]: state => ({
		...state,
		items: state.items.filter(item => !item.isNew)
	}),
	[actions.setUpdate]: (state, {payload}) => ({
		...state,
		items: state.items.map(item => {
			if ((payload.isNew && item.isNew) || item.uuid === payload.setID) {
				return {
					...item,
					...{
						...payload,
						setID: undefined
					}
				};
			} else {
				return item;
			}
		})
	})
}, {
	items: [],
	retrieving: false,
	lastSave: null
});

const versions = handleActions({
	[actions.versionListRetrieving]: (state, {payload}) => ({
		...state,
		items: [],
		retrieving: true
	}),
	[actions.versionListRetrieved]: (state, {payload}) => {
		const existingItemsByUuid = keyBy(state.items, 'uuid');
		
		return {
			...state,
			// Need to merge items with updated list, as version list endpoint doesn't retrieve items
			items: payload.map(item => ({
				...item,
				items: existingItemsByUuid[item.uuid] && existingItemsByUuid[item.uuid].items && [...existingItemsByUuid[item.uuid].items]
			})),
			retrieving: false
		};
	},
	[actions.versionSaving]: (state, {payload}) => ({
		...state,
		saving: payload
	}),
	[actions.versionSaved]: state => ({
		...state,
		saving: null,
		lastSave: new Date().getTime()
	}),
	[actions.versionSaveCleared]: state => ({
		...state,
		saving: null
	}),
	[actions.versionSaveCancelled]: state => ({
		...state,
		saving: null
	}),
	[actions.versionNew]: state => ({
		...state,
		items: [...state.items.filter(item => !item.isNew), {
			uuid: 'new',
			name: 'Version ' + ((state.items && state.items.length ? state.items.map(i => i.isNew ? 0 : i.number).sort(descCompareNumbers)[0] || 0 : 0) + 1),
			number: (state.items && state.items.length ? state.items.map(i => i.isNew ? 0 : i.number).sort(descCompareNumbers)[0] || 0 : 0) + 1,
			status: 'draft',
			approvals: [],
			notes: '',
			isNew: true
		}]
	}),
	[actions.versionNewSaved]: state => ({
		...state,
		items: state.items.map(item => {
			if (item.isNew) {
				return {
					...item,
					isNew: undefined
				};
			} else {
				return item;
			}
		})
	}),
	[actions.versionRemoveNew]: state => ({
		...state,
		items: state.items.filter(item => !item.isNew)
	}),
	[actions.versionUpdate]: (state, {payload}) => ({
		...state,
		items: state.items.map(item => {
			if (item.uuid === payload.versionID) {
				return {
					...item,
					...{
						...payload,
						versionID: undefined
					}
				};
			} else {
				return item;
			}
		})
	})
}, {
	items: [],
	retrieving: false,
	lastSave: null
});

const categories = handleActions({
	[actions.categoriesRetrieved]: (state, {payload}) => payload,
	[actions.categoriesAdd]: (state, {payload}) => (!payload || state.indexOf(payload) >= 0) ? state : [...state, payload].sort()
}, null);

export const reducer = combineReducers({
	sets,
	versions,
	categories
});


// WEBPACK FOOTER //
// ./src/syllabus/syllabus.state.js